import { FilterItem, FilterItemOperator, IStorageLocal } from "namirasoft-core";

type Listener = () => void;

const STORAGE_KEY = "ns-erp-scope";

export enum ErpScopeMode
{
    None = "none",
    View = "view",
    Select = "select",
    Filter = "filter",
}

export enum ErpScopeField
{
    Company = "company",
    Branch = "branch",
    FiscalYear = "fiscal_year",
}

export interface ScopeValue
{
    company_id: string | null;
    branch_id: string | null;
    fiscal_year_id: string | null;
    company_name: string | null;
    branch_name: string | null;
    fiscal_year_name: string | null;
}

type ScopeStore = { [M in ErpScopeMode]: ScopeValue };

function emptyValue(): ScopeValue
{
    return {
        company_id: null, branch_id: null, fiscal_year_id: null,
        company_name: null, branch_name: null, fiscal_year_name: null,
    };
}

function emptyStore(): ScopeStore
{
    return {
        [ErpScopeMode.None]: emptyValue(),
        [ErpScopeMode.View]: emptyValue(),
        [ErpScopeMode.Select]: emptyValue(),
        [ErpScopeMode.Filter]: emptyValue(),
    };
}

export class ErpScope
{
    private static storage = new IStorageLocal();
    private static listeners: Set<Listener> = new Set();
    private static store: ScopeStore = ErpScope.load();
    private static currentMode: ErpScopeMode = ErpScopeMode.Select;

    private static load(): ScopeStore
    {
        try
        {
            let raw = new IStorageLocal().get(STORAGE_KEY, "");
            if (!raw)
                return emptyStore();
            let parsed = JSON.parse(raw) as Partial<ScopeStore> | Partial<ScopeValue>;
            let result = emptyStore();
            for (let mode of [ErpScopeMode.None, ErpScopeMode.View, ErpScopeMode.Select, ErpScopeMode.Filter])
            {
                let slot = (parsed as any)[mode] as Partial<ScopeValue> | undefined;
                if (slot)
                {
                    result[mode] = {
                        company_id: slot.company_id ?? null,
                        branch_id: slot.branch_id ?? null,
                        fiscal_year_id: slot.fiscal_year_id ?? null,
                        company_name: slot.company_name ?? null,
                        branch_name: slot.branch_name ?? null,
                        fiscal_year_name: slot.fiscal_year_name ?? null,
                    };
                }
            }
            return result;
        }
        catch
        {
            return emptyStore();
        }
    }

    private static save(): void
    {
        ErpScope.storage.set(STORAGE_KEY, JSON.stringify(ErpScope.store));
    }

    private static notify(): void
    {
        for (let l of ErpScope.listeners) l();
    }

    private static current(): ScopeValue
    {
        return ErpScope.store[ErpScope.currentMode];
    }

    static setMode(mode: ErpScopeMode): void
    {
        if (ErpScope.currentMode === mode)
            return;
        ErpScope.currentMode = mode;
        ErpScope.notify();
    }

    static getMode(): ErpScopeMode { return ErpScope.currentMode; }

    static get(): ScopeValue
    {
        return { ...ErpScope.current() };
    }

    static getCompanyIdRaw(): string | null { return ErpScope.current().company_id; }
    static getBranchIdRaw(): string | null { return ErpScope.current().branch_id; }
    static getFiscalYearIdRaw(): string | null { return ErpScope.current().fiscal_year_id; }

    static getCompanyName(): string | null { return ErpScope.current().company_name; }
    static getBranchName(): string | null { return ErpScope.current().branch_name; }
    static getFiscalYearName(): string | null { return ErpScope.current().fiscal_year_name; }

    static getCompanyId(): string
    {
        let id = ErpScope.current().company_id;
        if (!id)
            throw new Error("Please select a company from the scope bar.");
        return id;
    }

    static getBranchId(): string
    {
        let id = ErpScope.current().branch_id;
        if (!id)
            throw new Error("Please select a branch from the scope bar.");
        return id;
    }

    static getFiscalYearId(): string
    {
        let id = ErpScope.current().fiscal_year_id;
        if (!id)
            throw new Error("Please select a fiscal year from the scope bar.");
        return id;
    }

    static getCompanyIdOrNull(): string | null { return ErpScope.current().company_id; }
    static getBranchIdOrNull(): string | null { return ErpScope.current().branch_id; }
    static getFiscalYearIdOrNull(): string | null { return ErpScope.current().fiscal_year_id; }

    static set(next: Partial<ScopeValue>): void
    {
        let mode = ErpScope.currentMode;
        let prev = ErpScope.store[mode];
        let merged: ScopeValue = {
            company_id: next.company_id !== undefined ? next.company_id : prev.company_id,
            branch_id: next.branch_id !== undefined ? next.branch_id : prev.branch_id,
            fiscal_year_id: next.fiscal_year_id !== undefined ? next.fiscal_year_id : prev.fiscal_year_id,
            company_name: next.company_name !== undefined ? next.company_name : prev.company_name,
            branch_name: next.branch_name !== undefined ? next.branch_name : prev.branch_name,
            fiscal_year_name: next.fiscal_year_name !== undefined ? next.fiscal_year_name : prev.fiscal_year_name,
        };

        if (merged.company_id !== prev.company_id)
        {
            if (next.branch_id === undefined) { merged.branch_id = null; merged.branch_name = null; }
            if (next.fiscal_year_id === undefined) { merged.fiscal_year_id = null; merged.fiscal_year_name = null; }
        }
        if (merged.company_id === null) merged.company_name = null;
        if (merged.branch_id === null) merged.branch_name = null;
        if (merged.fiscal_year_id === null) merged.fiscal_year_name = null;

        ErpScope.store[mode] = merged;
        ErpScope.save();
        ErpScope.notify();
    }

    static subscribe(listener: Listener): () => void
    {
        ErpScope.listeners.add(listener);
        return () => ErpScope.listeners.delete(listener);
    }

    static pushFilter(
        filters: FilterItem[] | null,
        table: { columns: { [k: string]: any } },
        column_name: "company_id" | "branch_id" | "fiscal_year_id",
        id: string | null,
    ): FilterItem[]
    {
        let f: FilterItem[] = filters ? [...filters] : [];
        if (!id)
            return f;
        let col = (table.columns as any)[column_name];
        if (!col)
            return f;
        f.push(new FilterItem(table as any, col, false, FilterItemOperator.all.equals, id));
        return f;
    }
}